import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TicketAuditEntry } from '@nbh/shared';
import { describeDay, formatTime } from '../../lib/format';
import { useNow } from '../../hooks/useNow';
import { LIFECYCLE_ACTIONS, actionPhrase, actorName } from '../pwa/auditPhrase';
import { ChatBubble } from './ChatBubble';
import { dayText } from './dayText';
import { type ThreadItem, type ThreadMessage, buildThread } from './threadModel';

export interface ChatThreadProps {
  messages: ThreadMessage[];
  /** Who is reading — decides which bubbles are "mine". Undefined while the session resolves. */
  viewerId: string | undefined;
  /**
   * Ticket chat is multi-party: received bubbles carry the sender's name and lifecycle
   * events interleave as system rows. A 1:1 DM is not, and shows neither.
   */
  multiParty: boolean;
  /** The job's audit trail, ticket chat only. Filtered here to lifecycle actions. */
  events?: TicketAuditEntry[];
  /**
   * The server's page size, when the endpoint has one. A thread that fills it says so at
   * the top rather than silently dropping the oldest — a message lost in an argument
   * about who said what is worse than a line admitting the list is capped.
   */
  truncatedAt?: number;
  emptyText: string;
}

/**
 * The one thread body both chats render (D1 of the chat brief): bubbles, day separators,
 * run grouping and — in ticket chat — the lifecycle inline. Ticket chat and DMs differ
 * only by the flag; two thread components would drift exactly as two badge components
 * would.
 */
export function ChatThread({
  messages,
  viewerId,
  multiParty,
  events,
  truncatedAt,
  emptyText,
}: ChatThreadProps) {
  const { t } = useTranslation();
  // Day labels are relative to now, and "Today" has to become "Yesterday" at midnight
  // on a screen left open. One clock for the whole thread.
  const now = useNow(60_000);

  const lifecycle = useMemo(
    () => (multiParty ? (events ?? []).filter((entry) => LIFECYCLE_ACTIONS.has(entry.action)) : []),
    [events, multiParty],
  );
  const items = useMemo(() => buildThread(messages, lifecycle), [messages, lifecycle]);

  if (items.length === 0) {
    return <p className="p-4 text-sm text-slate-500">{emptyText}</p>;
  }

  return (
    <ol className="flex flex-col px-3 py-2" aria-label={t('chat.threadLabel')}>
      {truncatedAt !== undefined && messages.length >= truncatedAt && (
        <li role="note" className="py-2 text-center text-[11px] text-slate-500">
          {t('chat.showingLatest', { count: truncatedAt })}
        </li>
      )}
      {items.map((item) => (
        <ThreadRow key={keyOf(item)} item={item} viewerId={viewerId} multiParty={multiParty} now={now} />
      ))}
    </ol>
  );
}

function keyOf(item: ThreadItem): string {
  if (item.kind === 'day') return `day-${item.dayKey}`;
  if (item.kind === 'event') return `event-${item.entry.id}`;
  return `message-${item.message.id}`;
}

function ThreadRow({
  item,
  viewerId,
  multiParty,
  now,
}: {
  item: ThreadItem;
  viewerId: string | undefined;
  multiParty: boolean;
  now: Date;
}) {
  const { t } = useTranslation();

  if (item.kind === 'day') {
    return (
      <li data-kind="day" className="my-3 flex justify-center">
        <span className="rounded-full bg-slate-800/90 px-3 py-1 text-[11px] font-medium text-slate-300">
          {dayText(describeDay(item.at, now), t)}
        </span>
      </li>
    );
  }

  if (item.kind === 'event') {
    const { entry } = item;
    return (
      // The WhatsApp "missed call" card: something that happened in the conversation,
      // visibly not something somebody said. Reason included — for a hold it is the point.
      <li data-kind="system" className="my-2 flex justify-center px-4 text-center">
        <p className="rounded-lg bg-slate-900/80 px-3 py-1 text-[11px] leading-4 text-slate-400">
          <span className="font-medium text-slate-300">{actorName(entry, t)}</span> {actionPhrase(entry, t)}
          {entry.reason ? ` — ${entry.reason}` : ''}
          <span className="tabular-nums"> · {formatTime(entry.createdAt)}</span>
        </p>
      </li>
    );
  }

  const mine = viewerId !== undefined && item.message.senderId === viewerId;
  return (
    <li data-kind="message">
      <ChatBubble
        message={item.message}
        mine={mine}
        showSender={multiParty && !mine && item.firstOfRun}
        tail={item.firstOfRun}
      />
    </li>
  );
}
